import React, { useState, useEffect } from 'react';
import { auth, db } from '../supabaseClient';
import Dashboard from './Dashboard';
import CategoryManager from './CategoryManager';
import LoginForm from './LoginForm';

const App = () => {
  const [user, setUser] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, categories
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // 初始化数据
  useEffect(() => {
    const initApp = async () => {
      try {
        const currentUser = await auth.getCurrentUser();
        setUser(currentUser);
        
        if (currentUser) {
          await loadData(currentUser.id);
        }
      } catch (error) {
        console.error('初始化失败:', error);
      } finally {
        setLoading(false);
      }
    };

    initApp();
  }, []);

  // 登录成功处理
  const handleLogin = (loggedInUser) => {
    setUser(loggedInUser);
    loadData(loggedInUser.id);
  };

  // 加载所有数据
  const loadData = async (userId) => {
    try {
      const [categoriesRes, itemsRes] = await Promise.all([
        db.getCategories(userId),
        db.getItems(userId)
      ]);

      if (categoriesRes.data) setCategories(categoriesRes.data);
      if (itemsRes.data) setItems(itemsRes.data);
    } catch (error) {
      console.error('加载数据失败:', error);
    }
  };

  // 构建分类树
  const buildCategoryTree = (categories, items) => {
    const categoryMap = {};
    const rootCategories = [];
    
    // 创建映射
    categories.forEach(cat => {
      categoryMap[cat.id] = {
        ...cat,
        children: [],
        items: items.filter(item => item.category_id === cat.id)
      };
    });
    
    // 构建树结构
    categories.forEach(cat => {
      const category = categoryMap[cat.id];
      if (cat.parent_id && categoryMap[cat.parent_id]) {
        categoryMap[cat.parent_id].children.push(category);
      } else {
        rootCategories.push(category);
      }
    });
    
    return rootCategories;
  };

  // 生成品牌颜色
  const generateBrandColor = (brand) => {
    if (!brand) return '#00FFFF';
    
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2',
      '#F8B739', '#52B788', '#E76F51', '#8ECAE6', '#219EBC'
    ];
    
    let hash = 0;
    for (let i = 0; i < brand.length; i++) {
      hash = brand.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-cyan-400 text-xl animate-pulse">
          系统初始化中...
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const categoryTree = buildCategoryTree(categories, items);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121212' }}>
      {/* 主内容 */}
      <div className="relative">
        {/* 顶部导航 */}
        <header className="border-b" style={{ borderColor: '#2A2A2A', backgroundColor: '#18181B' }}>
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <h1 className="text-2xl font-bold" style={{ color: '#FFFFFF' }}>
                  日常好物档案
                </h1>
                <span className="text-xs px-2 py-1 rounded" style={{ color: '#A0A0A0', backgroundColor: '#2A2A2A' }}>
                  极简版
                </span>
              </div>
              
              <div className="flex items-center space-x-4">
                {/* 导航按钮 */}
                <button
                  onClick={() => setCurrentView('dashboard')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentView === 'dashboard' 
                      ? 'bg-white text-black' 
                      : 'hover:bg-gray-700'
                  }`}
                  style={{ 
                    backgroundColor: currentView === 'dashboard' ? '#FFFFFF' : '#4A4A4A',
                    color: currentView === 'dashboard' ? '#121212' : '#FFFFFF'
                  }}
                >
                  看板视图
                </button>
                
                <button
                  onClick={() => setCurrentView('categories')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    currentView === 'categories' 
                      ? 'bg-white text-black' 
                      : 'hover:bg-gray-700'
                  }`}
                  style={{ 
                    backgroundColor: currentView === 'categories' ? '#FFFFFF' : '#4A4A4A',
                    color: currentView === 'categories' ? '#121212' : '#FFFFFF'
                  }}
                >
                  分类管理
                </button>
                
                <span className="text-sm" style={{ color: '#A0A0A0' }}>
                  {user.email}
                </span>
                
                <button
                  onClick={async () => {
                    await auth.signOut();
                    window.location.reload();
                  }}
                  className="px-4 py-2 rounded-lg transition-colors"
                  style={{ backgroundColor: '#4A4A4A', color: '#FFFFFF' }}
                >
                  登出
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* 主内容区 */}
        <main className="p-6">
          {currentView === 'dashboard' && (
            <Dashboard
              categoryTree={categoryTree}
              categories={categories}
              items={items}
              generateBrandColor={generateBrandColor}
              onRefresh={() => loadData(user.id)}
            />
          )}
          
          {currentView === 'categories' && (
            <CategoryManager
              categories={categories}
              onRefresh={() => loadData(user.id)}
            />
          )}
        </main>
      </div>

      {/* 自定义样式 */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image: 
            linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        /* 自定义滚动条 */
        ::-webkit-scrollbar {
          height: 8px;
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: rgba(0, 255, 255, 0.3);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default App;
